import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, LoadingModule, SidebarModal, StatusBadge, Text,
} from '@domain-group/suit-theme'

import useArtworks from '@hooks/useArtworks'
import useSelector from '@hooks/useSelector'

import type { TemplatePageModel } from '@models/types'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalPayload = {
  templatePage?: TemplatePageModel,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  payload?: ModalPayload,
  showModal: boolean,
}

const ArtworkInUseModal = (props: ModalProps) => {
  const { callbacks, payload, showModal } = props
  const { closeModal } = callbacks

  const {
    templatePage,
  } = payload || {}

  const { id } = templatePage || {}

  const { filteredArtworks, canLoadMore, callbacks: { loadMore }, loading } = useArtworks({
    filters: {
      template_page_id: id,
      order_item_statuses: ['pending'],
    },
    requestOptions: { include_artwork_campaign: true, include_artwork_order: true, include_order_items: true },
    performHttpRequests: !!templatePage?.id,
  })

  const orders = useSelector(reduxState => reduxState.entities.orders)

  const firstArtworkOrderId = (filteredArtworks[0])?.order_id
  const order = firstArtworkOrderId ? orders[firstArtworkOrderId] : undefined

  const artworksInUseCount = Number(
    templatePage?.in_use_count ?? 0,
  )

  const campaigns = useSelector(reduxState => reduxState.entities.campaigns)

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey="ArtworkInUseModal" showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Artwork" titleSecondLine="In Use" />

      <SidebarModal.SubHeader>
        <Box boxProps={{ lineHeight: 1.3, marginBottom: 'large' }} flexDirection="column">
          <Box flexDirection="row">
            <Text fontSize="small">
              {templatePage?.title}
            </Text>

            <Text color="bodyFontLightColor" fontSize="small" style={{ marginLeft: '4px' }}>
              {`(#${templatePage?.id})`}
            </Text>
          </Box>

          <StatusBadge
            boxProps={{ marginTop: 'medium' }}
            color="grey"
            ghost
            text={`In use: ${artworksInUseCount} artworks`}
          />
        </Box>
      </SidebarModal.SubHeader>

      <SidebarModal.Content>
        {loading && !filteredArtworks.length && (
          <LoadingModule loading={loading} times={1} />
        )}

        {filteredArtworks.map((artwork) => {
          const campaign = campaigns[artwork.campaign_id]

          return (
            <SidebarModal.ListItem
              boxProps={{ padding: 'medium' }}
              key={artwork.id}
              secondaryText={`Artwork ID: ${artwork.id} - Order ID: ${order?.id ?? 'N/A'}`}
              title={campaign?.title}
            />
          )})}

          {canLoadMore && (
            <Box boxProps={{ padding: 'medium', marginTop: 'medium' }}>
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faSpinner} />}
                onClick={loadMore}
                size="small"
              >
                Load More
              </Button>
            </Box>
          )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button buttonStyle="secondaryUtility" onClick={() => closeModal()} size="large">
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  // @ts-expect-error TS2786
  <SidebarModal.RenderController {...props}>
    <ArtworkInUseModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
